<template>
  <div>
    <AppTitulo titulo="Reprocesso PNCP" />

    <section class="section">
      <div class="container is-fluid">
        <div class="columns">
          <div class="column is-8 is-offset-2">
            <div class="box">
              <b-message type="is-info" has-icon :closable="false">
                Modo avançado: enfileira um job PNCP na API v1 (ARP e entityType manual). Para
                consultar um pregão e reprocessar PNCP/+Brasil pelo ID,
                <router-link :to="{ name: 'AdminProcurementIntegrations' }">
                  use a consulta por pregão</router-link
                >. Informe <strong>entityType</strong>, <strong>entityId</strong> e
                <strong>operation</strong>. Para entidades filhas, informe também o
                <strong>parentEntityId</strong> (ID da compra / <code>OfertasCompra.id</code>).
              </b-message>

              <b-message
                v-if="entityHint"
                type="is-warning"
                has-icon
                :closable="false"
                class="mb-3"
              >
                {{ entityHint }}
              </b-message>

              <b-field grouped group-multiline>
                <b-field expanded label="Tipo de entidade">
                  <b-select v-model="form.entityType" expanded placeholder="Selecione">
                    <option
                      v-for="entityType in entityTypeOptions"
                      :key="entityType"
                      :value="entityType"
                    >
                      {{ entityType }}
                    </option>
                  </b-select>
                </b-field>

                <b-field expanded :label="entityIdLabel">
                  <b-input
                    v-model.trim="form.entityId"
                    type="number"
                    min="1"
                    :placeholder="entityIdPlaceholder"
                  />
                </b-field>
              </b-field>

              <b-field
                v-if="requiresParentEntityId"
                label="ID da compra (parentEntityId)"
                message="Obrigatório para item, documento e ARP. Use OfertasCompra.id."
              >
                <b-input
                  v-model.trim="form.parentEntityId"
                  type="number"
                  min="1"
                  placeholder="Ex.: 625"
                />
              </b-field>

              <b-field grouped group-multiline>
                <b-field expanded label="Operação">
                  <b-select v-model="form.operation" expanded placeholder="Selecione">
                    <option
                      v-for="operation in operationOptions"
                      :key="operation"
                      :value="operation"
                    >
                      {{ operation }}
                    </option>
                  </b-select>
                </b-field>

                <b-field
                  expanded
                  label="Processo (opcional)"
                  message="Se omitido, a API deriva do tipo da compra."
                >
                  <b-select v-model="form.processType" expanded placeholder="Selecione">
                    <option value="">—</option>
                    <option
                      v-for="processType in processTypeOptions"
                      :key="processType"
                      :value="processType"
                    >
                      {{ processType }}
                    </option>
                  </b-select>
                </b-field>
              </b-field>

              <div class="is-flex is-justify-content-flex-end">
                <b-button type="is-primary" :loading="loading.submit" @click.prevent="handleSubmit">
                  Reprocessar
                </b-button>
              </div>
            </div>

            <div v-if="responsePayload" class="box">
              <h2 class="title is-6">Resposta</h2>
              <pre class="response-preview">{{ responsePayload }}</pre>
            </div>

            <div class="box">
              <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
                <h2 class="title is-6 mb-0">Últimos reprocessos</h2>
                <b-button
                  size="is-small"
                  type="is-text"
                  :disabled="!historyItems.length"
                  @click.prevent="clearHistory"
                >
                  Limpar histórico
                </b-button>
              </div>

              <b-table
                :data="historyItems"
                :mobile-card="false"
                :paginated="historyItems.length > 5"
                :per-page="5"
                default-sort="createdAt"
                default-sort-direction="desc"
              >
                <template slot-scope="props">
                  <b-table-column label="Data/Hora" field="createdAt" sortable>
                    {{ formatDateTime(props.row.createdAt) }}
                  </b-table-column>

                  <b-table-column label="Alvo" field="target">
                    {{ props.row.target }}
                  </b-table-column>

                  <b-table-column label="Status" field="status" sortable>
                    <b-tag :type="props.row.success ? 'is-success' : 'is-danger'">
                      {{ props.row.status }}
                    </b-tag>
                  </b-table-column>

                  <b-table-column label="Ações">
                    <b-button
                      size="is-small"
                      type="is-text"
                      @click.prevent="reuseFromHistory(props.row)"
                    >
                      Reutilizar
                    </b-button>
                  </b-table-column>
                </template>

                <template #empty>
                  <div class="has-text-centered has-text-grey">Sem reprocessos registrados.</div>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import AppTitulo from '@/components/AppTitulo';

const ENTITY_TYPES = [
  'AUCTION',
  'AUCTION_ITEM',
  'AUCTION_DOCUMENT',
  'ARP_ATA',
  'ARP_DOCUMENT',
  'ARP_INVOLVED_PARTY',
];

const PROCESS_TYPES = ['PREGAO', 'CONCORRENCIA', 'DISPENSA'];

const OPERATIONS_BY_ENTITY = {
  AUCTION: ['CREATE', 'UPDATE', 'SYNC'],
  AUCTION_ITEM: ['CREATE', 'UPDATE', 'SYNC'],
  AUCTION_DOCUMENT: ['CREATE', 'DELETE'],
  ARP_ATA: ['CREATE', 'UPDATE', 'DELETE'],
  ARP_DOCUMENT: ['CREATE', 'DELETE'],
  ARP_INVOLVED_PARTY: ['CREATE', 'DELETE'],
};

const ENTITY_DEFAULTS = {
  AUCTION: {
    operation: 'SYNC',
    processType: 'PREGAO',
  },
  AUCTION_ITEM: {
    operation: 'UPDATE',
    processType: 'PREGAO',
  },
  AUCTION_DOCUMENT: {
    operation: 'CREATE',
    processType: 'PREGAO',
  },
  ARP_ATA: {
    operation: 'UPDATE',
    processType: 'PREGAO',
  },
  ARP_DOCUMENT: {
    operation: 'CREATE',
    processType: 'PREGAO',
  },
  ARP_INVOLVED_PARTY: {
    operation: 'CREATE',
    processType: 'PREGAO',
  },
};

const ENTITY_HINTS = {
  AUCTION:
    'entityId = OfertasCompra.id. SYNC/UPDATE atualizam a compra; CREATE só se ainda não houver idExterno.',
  AUCTION_ITEM:
    'entityId = OfertasCompraItens.id. Resultado homologado sobe no UPDATE/SYNC do item (não use ITEM_RESULT). parentEntityId = OfertasCompra.id.',
  AUCTION_DOCUMENT:
    'entityId = OfertasCompraAnexos.id. CREATE de EDITAL remove o edital INATIVO anterior no PNCP. parentEntityId = OfertasCompra.id.',
  ARP_ATA: 'entityId = OfertasCompraAtasPrecos.id. parentEntityId = OfertasCompra.id.',
  ARP_DOCUMENT:
    'entityId = OfertasCompraAtasPrecosDocumentos.id. parentEntityId = OfertasCompra.id.',
  ARP_INVOLVED_PARTY:
    'entityId = OfertasCompraAtasPrecosPartesEnvolvidas.id. parentEntityId = OfertasCompra.id.',
};

const ENTITY_ID_LABELS = {
  AUCTION: 'ID da compra (entityId)',
  AUCTION_ITEM: 'ID do item (entityId)',
  AUCTION_DOCUMENT: 'ID do anexo (entityId)',
  ARP_ATA: 'ID da ata (entityId)',
  ARP_DOCUMENT: 'ID do documento da ata (entityId)',
  ARP_INVOLVED_PARTY: 'ID da parte envolvida (entityId)',
};

const HISTORY_STORAGE_KEY = 'adminPncpReprocessHistory';

export default {
  name: 'AdminPncpReprocess',
  components: {
    AppTitulo,
  },
  data() {
    return {
      loading: {
        submit: false,
      },
      form: {
        entityType: '',
        entityId: '',
        parentEntityId: '',
        operation: '',
        processType: '',
      },
      responsePayload: '',
      historyItems: [],
    };
  },
  computed: {
    entityTypeOptions() {
      return ENTITY_TYPES;
    },
    processTypeOptions() {
      return PROCESS_TYPES;
    },
    operationOptions() {
      if (!this.form.entityType) {
        return [];
      }

      return OPERATIONS_BY_ENTITY[this.form.entityType] || [];
    },
    requiresParentEntityId() {
      return !!this.form.entityType && this.form.entityType !== 'AUCTION';
    },
    entityHint() {
      return ENTITY_HINTS[this.form.entityType] || '';
    },
    entityIdLabel() {
      return ENTITY_ID_LABELS[this.form.entityType] || 'ID da entidade (entityId)';
    },
    entityIdPlaceholder() {
      if (this.form.entityType === 'AUCTION') return 'Ex.: 625';
      return 'Ex.: 12345';
    },
  },
  created() {
    this.loadHistory();
  },
  watch: {
    'form.entityType': function formEntityTypeWatcher(newEntityType, oldEntityType) {
      if (!newEntityType || newEntityType === oldEntityType) return;

      const defaults = ENTITY_DEFAULTS[newEntityType];
      const allowedOperations = OPERATIONS_BY_ENTITY[newEntityType] || [];

      if (!this.form.operation || allowedOperations.indexOf(this.form.operation) === -1) {
        this.form.operation = defaults ? defaults.operation : '';
      }

      if (!this.form.processType && defaults) {
        this.form.processType = defaults.processType;
      }

      if (newEntityType === 'AUCTION') {
        this.form.parentEntityId = '';
      }
    },
  },
  methods: {
    ...mapActions('pncpReprocess', ['reprocessPncp']),
    formatDateTime(value) {
      if (!value) return '-';

      return new Date(value).toLocaleString('pt-BR');
    },
    getErrorMessage(error) {
      if (error && error.response && error.response.data) {
        const { data } = error.response;

        if (typeof data === 'string') return data;
        if (data.message) return data.message;
        if (data.error) return data.error;
        if (data.errors && Array.isArray(data.errors) && data.errors.length) {
          const [firstError] = data.errors;
          if (typeof firstError === 'string') return firstError;
          if (firstError && firstError.message) return firstError.message;
        }

        return JSON.stringify(data);
      }

      if (error && error.message) return error.message;
      return 'Não foi possível solicitar o reprocesso.';
    },
    buildHistoryTarget(payload) {
      if (!payload.entityType || !payload.entityId) return '-';

      const parentSuffix = payload.parentEntityId ? ` (parent #${payload.parentEntityId})` : '';

      return `${payload.entityType} #${payload.entityId}${parentSuffix} · ${
        payload.operation || '-'
      }`;
    },
    appendHistory({ payload, success, status, response }) {
      const item = {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        createdAt: new Date().toISOString(),
        payload,
        success,
        status,
        target: this.buildHistoryTarget(payload),
        response,
      };

      this.historyItems = [item, ...this.historyItems].slice(0, 10);
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(this.historyItems));
    },
    loadHistory() {
      try {
        const history = localStorage.getItem(HISTORY_STORAGE_KEY);
        this.historyItems = history ? JSON.parse(history) : [];
      } catch (_error) {
        this.historyItems = [];
      }
    },
    clearHistory() {
      this.historyItems = [];
      localStorage.removeItem(HISTORY_STORAGE_KEY);
    },
    reuseFromHistory(historyItem) {
      if (!historyItem || !historyItem.payload) return;

      const { payload } = historyItem;
      const entityType =
        payload.entityType === 'ITEM_RESULT' ? 'AUCTION_ITEM' : payload.entityType || '';

      this.form.entityType = entityType;
      this.form.entityId =
        payload.entityId !== undefined && payload.entityId !== null ? String(payload.entityId) : '';
      this.form.parentEntityId =
        payload.parentEntityId !== undefined && payload.parentEntityId !== null
          ? String(payload.parentEntityId)
          : '';
      this.form.operation = payload.operation || '';
      this.form.processType = payload.processType || '';

      if (entityType) {
        const allowedOperations = OPERATIONS_BY_ENTITY[entityType] || [];
        if (!this.form.operation || allowedOperations.indexOf(this.form.operation) === -1) {
          const defaults = ENTITY_DEFAULTS[entityType];
          this.form.operation = defaults ? defaults.operation : '';
        }
      }
    },
    buildPayload() {
      const payload = {
        entityType: this.form.entityType,
        entityId: Number(this.form.entityId),
        operation: this.form.operation,
      };

      if (this.requiresParentEntityId && this.form.parentEntityId) {
        payload.parentEntityId = Number(this.form.parentEntityId);
      }

      if (this.form.processType) {
        payload.processType = this.form.processType;
      }

      return payload;
    },
    validateForm() {
      if (!this.form.entityType || !this.form.entityId || !this.form.operation) {
        return 'Informe entityType, entityId e operation.';
      }

      if (!Number.isInteger(Number(this.form.entityId)) || Number(this.form.entityId) <= 0) {
        return 'entityId deve ser um inteiro positivo.';
      }

      const allowedOperations = OPERATIONS_BY_ENTITY[this.form.entityType] || [];
      if (allowedOperations.indexOf(this.form.operation) === -1) {
        return `Operação ${this.form.operation} não é suportada para ${this.form.entityType}.`;
      }

      if (this.requiresParentEntityId) {
        if (!this.form.parentEntityId) {
          return 'Informe parentEntityId (OfertasCompra.id) para esta entidade.';
        }

        if (
          !Number.isInteger(Number(this.form.parentEntityId)) ||
          Number(this.form.parentEntityId) <= 0
        ) {
          return 'parentEntityId deve ser um inteiro positivo.';
        }
      }

      return null;
    },
    async handleSubmit() {
      const validationError = this.validateForm();
      if (validationError) {
        this.$alerta(validationError, 'is-danger');
        return;
      }

      this.loading.submit = true;
      this.responsePayload = '';

      const payload = this.buildPayload();

      try {
        const response = await this.reprocessPncp(payload);
        this.responsePayload = response
          ? JSON.stringify(response, null, 2)
          : 'Reprocesso enviado com sucesso.';
        this.$alerta('Solicitação de reprocesso enviada.', 'is-success');
        this.appendHistory({
          payload,
          success: true,
          status: 'Sucesso',
          response,
        });
      } catch (error) {
        const errorMessage = this.getErrorMessage(error);
        this.$alerta(errorMessage, 'is-danger');
        this.appendHistory({
          payload,
          success: false,
          status: 'Erro',
          response: errorMessage,
        });
      } finally {
        this.loading.submit = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.response-preview {
  max-height: 360px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-3 {
  margin-bottom: 1rem;
}
</style>
